import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormContext } from "react-hook-form";
import Button from "../../../../components/Buttons/Button";
import { useTranslationApp } from "../../../../lib/i18next";
import { CAMPAIGN_APPROVAL_PANEL_STALE_TOGGLE } from "../../../../i18n/keysTranslations";
import {
  getApprovalCommentEntityLines,
  getCampaignApprovalFieldLabel,
  mapApprovalTargetToFieldPath,
  mapApprovalTargetToSection,
} from "../../../CampaignApproval/utils";
import {
  deriveApprovalStatusFromReviewers,
  hasMultipleApprovalReviewers,
} from "../../../../components/ContentApproval/approvalReviewersUtils";
import focusFieldByPath from "../../utils/focusFieldByPath";
import ApprovalCommentFieldHint from "./ApprovalCommentFieldHint";
import { useApprovalCommentFieldHint } from "./approvalCommentFieldHintContext";
import CampaignApprovalCommentCard from "./CampaignApprovalCommentCard";
import ApprovalReviewersStatus from "../../../../components/ContentApproval/ApprovalReviewersStatus";
import { getCampaignApprovalPanelCopy } from "./campaignApprovalPanelCopy";

const isDisplayableComment = (comment) =>
  comment?.type === "specific" || comment?.type === "general";

const getApprovalCommentFieldLabel = (target, t) => {
  if (!target?.fieldType) {
    return "";
  }

  const itemIndex =
    Number.isInteger(target?.fieldIndex) || Number.isInteger(target?.mediaIndex)
      ? (target?.fieldIndex ?? target?.mediaIndex)
      : null;

  return getCampaignApprovalFieldLabel(target.fieldType, t, itemIndex);
};

const CampaignApprovalCommentsContent = ({
  approvalData,
  onChangeSectionSelected,
}) => {
  const { t } = useTranslationApp();
  const { getValues } = useFormContext();
  const [showStaleComments, setShowStaleComments] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState("");
  const [fieldHint, setFieldHint] = useState({
    open: false,
    anchorEl: null,
    body: "",
  });
  const { registerOnClose } = useApprovalCommentFieldHint();
  const isMultiReviewer = hasMultipleApprovalReviewers(approvalData?.reviewers);
  const derivedGlobalStatus = deriveApprovalStatusFromReviewers({
    reviewers: approvalData?.reviewers,
    fallbackStatus: approvalData?.status,
  });
  const panelEmptyKey = getCampaignApprovalPanelCopy({
    isMultiReviewer,
    globalStatus: derivedGlobalStatus,
    copyType: "empty",
  });

  const closeFieldHint = useCallback(() => {
    setFieldHint({
      open: false,
      anchorEl: null,
      body: "",
    });
  }, []);

  useEffect(
    () => registerOnClose(closeFieldHint),
    [registerOnClose, closeFieldHint]
  );

  const activeComments = useMemo(() => {
    const currentDraftID = approvalData?.currentDraftID;
    return (approvalData?.comments || [])
      .filter(
        (comment) =>
          isDisplayableComment(comment) &&
          !comment?.isStale &&
          (!currentDraftID ||
            !comment?.draftID ||
            comment?.draftID === currentDraftID)
      )
      .sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0));
  }, [approvalData]);

  const staleComments = useMemo(() => {
    const currentDraftID = approvalData?.currentDraftID;
    return (approvalData?.comments || [])
      .filter(
        (comment) =>
          isDisplayableComment(comment) &&
          (comment?.isStale ||
            (currentDraftID &&
              comment?.draftID &&
              comment?.draftID !== currentDraftID))
      )
      .sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0));
  }, [approvalData]);

  const onNavigateToComment = (comment) => {
    if (comment?.type !== "specific" || !comment?.target) {
      return;
    }

    setSelectedCommentId(comment.id);
    closeFieldHint();

    const nextSection = mapApprovalTargetToSection({
      target: comment.target,
      adsGroups: getValues("adsGroups") || [],
    });

    if (!nextSection) {
      return;
    }

    onChangeSectionSelected?.(nextSection);

    const fieldPath = mapApprovalTargetToFieldPath({
      target: comment.target,
      getValues,
    });

    if (fieldPath) {
      focusFieldByPath(fieldPath, 8, (element) => {
        const anchorEl = element?.parentElement || element;

        if (!anchorEl?.isConnected) {
          return;
        }

        setFieldHint({
          open: true,
          anchorEl,
          body: comment?.body || "",
        });
      });
    }
  };

  const renderCommentCard = (comment, muted = false) => {
    const isNavigable =
      comment?.type === "specific" && Boolean(comment?.target);
    const isStale = muted || Boolean(comment?.isStale);

    if (comment?.type === "general") {
      return (
        <CampaignApprovalCommentCard
          key={comment.id}
          variant="general"
          body={comment?.body}
          reviewerEmail={comment?.reviewerEmail}
          isStale={isStale}
        />
      );
    }

    return (
      <CampaignApprovalCommentCard
        key={comment.id}
        variant="specific"
        body={comment?.body}
        reviewerEmail={comment?.reviewerEmail}
        platform={comment?.target?.platform}
        entityLines={getApprovalCommentEntityLines({
          target: comment?.target,
          getValues,
        })}
        fieldLabel={getApprovalCommentFieldLabel(comment?.target, t)}
        isSelected={selectedCommentId === comment.id}
        isStale={isStale}
        isNavigable={isNavigable}
        onClick={() => onNavigateToComment(comment)}
      />
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 1.5 }}>
          <ApprovalReviewersStatus reviewers={approvalData?.reviewers || []} />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            pr: 0.5,
          }}
        >
          {activeComments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t(panelEmptyKey)}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {activeComments.map((comment) => renderCommentCard(comment))}
            </Box>
          )}

          {staleComments.length > 0 && approvalData?.status !== "approved" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                type="button"
                color="secondary"
                onClick={() =>
                  setShowStaleComments((currentState) => !currentState)
                }
                sx={{
                  minWidth: "max-content",
                  alignSelf: "flex-start",
                  height: 36,
                  px: 2,
                }}
              >
                {t(CAMPAIGN_APPROVAL_PANEL_STALE_TOGGLE)}
              </Button>

              {showStaleComments ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {staleComments.map((comment) =>
                    renderCommentCard(comment, true)
                  )}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      <ApprovalCommentFieldHint
        open={fieldHint.open}
        anchorEl={fieldHint.anchorEl}
        body={fieldHint.body}
        onClose={closeFieldHint}
      />
    </>
  );
};

export default CampaignApprovalCommentsContent;
